/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MAILBOX TWO-WAY SYNC SERVICE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Handles bidirectional synchronization between the Dashboard and Sequencers
 * (Smartlead, Instantly, etc).
 * 
 * KEY PRINCIPLES:
 * 1. Dashboard is the source of truth - Dashboard always wins conflicts
 * 2. Conflicts are logged and alerted
 * 3. New mailboxes in sequencer are auto-imported with alerts
 * 4. Activity discrepancies are detected and reported
 * 5. All operations are tracked for audit trail
 * 
 * USAGE:
 *   const service = new MailboxTwoWaySyncService()
 *   const result = await service.performFullSync('smartlead', progressCallback)
 */

import { supabase } from '@/integrations/supabase/client'
import { apiKeyManager } from '@/lib/apiKeyManager'
import { formatSignatureForSync, areSignaturesEquivalent } from '@/lib/signature-formatter'
import {
  TwoWaySyncResult,
  TwoWaySyncProgress,
  TwoWaySyncProgressCallback,
  SyncConflict,
  DashboardToSequencerPushResult,
  SequencerType,
  MailboxSequencerConnection,
  SmartleadUpdatePayload,
  DASHBOARD_TO_SMARTLEAD_FIELD_MAP,
  SYNCABLE_DASHBOARD_FIELDS,
} from './two-way-sync-types'
import { SmartleadMailboxDetails } from './types'

// ═══════════════════════════════════════════════════════════════════════════
// MAIN TWO-WAY SYNC SERVICE
// ═══════════════════════════════════════════════════════════════════════════

export class MailboxTwoWaySyncService {
  private progressCallback?: TwoWaySyncProgressCallback
  private abortSignal?: AbortSignal
  
  constructor(progressCallback?: TwoWaySyncProgressCallback) {
    this.progressCallback = progressCallback
  }

  /**
   * Performs a complete two-way sync between Dashboard and Sequencer
   */
  async performFullSync(
    sequencerType: SequencerType = 'smartlead',
    abortSignal?: AbortSignal
  ): Promise<TwoWaySyncResult> {
    this.abortSignal = abortSignal
    const startedAt = new Date().toISOString()
    
    const result: TwoWaySyncResult = {
      success: false,
      message: '',
      mailboxes_synced: 0,
      conflicts_detected: 0,
      conflicts_resolved: 0,
      new_mailboxes_imported: 0,
      discrepancies_found: 0,
      alerts_created: 0,
      conflicts: [],
      imported_mailboxes: [],
      discrepancies: [],
      errors: [],
      started_at: startedAt,
      completed_at: '',
      duration_ms: 0,
    }

    try {
      // Starting two-way sync
      this.updateProgress('initializing', 0, 1, null, 'Initializing two-way sync...', 0, 0)

      // Check API key
      if (!apiKeyManager.getApiKey()) {
        throw new Error(`${sequencerType} API key not configured`)
      }

      // Phase 1: Fetch dashboard mailboxes
      this.updateProgress('fetching_dashboard', 0, 1, null, 'Fetching dashboard mailboxes...', 0, 0)
      const dashboardMailboxes = await this.fetchDashboardMailboxes()
      // Dashboard mailboxes fetched

      // Phase 2: Fetch sequencer mailboxes
      this.updateProgress('fetching_sequencer', 0, 1, null, `Fetching ${sequencerType} mailboxes...`, 0, 0)
      const sequencerMailboxes = await this.fetchSequencerMailboxes(sequencerType)
      // Sequencer mailboxes fetched

      const total = Math.max(dashboardMailboxes.length, sequencerMailboxes.length)

      // Phase 3: Compare and sync
      this.updateProgress('comparing', 0, total, null, 'Comparing mailboxes...', 0, 0)
      await this.compareAndSync(dashboardMailboxes, sequencerMailboxes, sequencerType, result)

      // Phase 4: Check for discrepancies (temporarily disabled - table not deployed)
      this.updateProgress('checking_discrepancies', 0, total, null, 'Skipping activity discrepancy check (table not deployed)...', 0, 0)
      // await this.checkActivityDiscrepancies(dashboardMailboxes, sequencerType, result)

      // Phase 5: Create alerts
      if (result.conflicts.length > 0 || result.imported_mailboxes.length > 0 || result.discrepancies.length > 0) {
        this.updateProgress('creating_alerts', 0, 1, null, 'Creating alerts...', result.conflicts_detected, result.alerts_created)
        await this.createAlerts(result, sequencerType)
      }

      // Finalize
      const completedAt = new Date().toISOString()
      result.completed_at = completedAt
      result.duration_ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
      result.success = true
      result.message = `Successfully synced ${result.mailboxes_synced} mailboxes with ${sequencerType}. ${result.conflicts_resolved} conflicts resolved, ${result.new_mailboxes_imported} new mailboxes imported.`

      this.updateProgress('completed', total, total, null, result.message, result.conflicts_detected, result.alerts_created)
      // Two-way sync completed

      return result

    } catch (error) {
      // Two-way sync failed
      result.success = false
      result.message = error instanceof Error ? error.message : 'Unknown error'
      result.errors.push(result.message)
      result.completed_at = new Date().toISOString()
      result.duration_ms = new Date(result.completed_at).getTime() - new Date(startedAt).getTime()
      return result
    }
  }

  /**
   * Compare mailboxes and perform sync operations
   */
  private async compareAndSync(
    dashboardMailboxes: any[],
    sequencerMailboxes: SmartleadMailboxDetails[],
    sequencerType: SequencerType,
    result: TwoWaySyncResult
  ): Promise<void> {
    const sequencerEmailMap = new Map(
      sequencerMailboxes.map(m => [m.from_email.toLowerCase(), m])
    )
    const dashboardEmailMap = new Map(
      dashboardMailboxes.map(m => [m.email.toLowerCase(), m])
    )

    let processed = 0
    const total = dashboardMailboxes.length + sequencerMailboxes.length

    // Process dashboard mailboxes in batches to reduce Edge Function calls
    // Use batch-update to reduce from 122 Edge Function calls to ~12 calls
    const batchSize = 10 // Process 10 mailboxes per batch to reduce Edge Function calls
    const dashboardBatches = this.chunkArray(dashboardMailboxes, batchSize)
    
    for (let batchIndex = 0; batchIndex < dashboardBatches.length; batchIndex++) {
      const batch = dashboardBatches[batchIndex]
      
      if (this.abortSignal?.aborted) throw new Error('Sync cancelled')
      
      this.updateProgress('pushing_to_sequencer', processed, total, null, 
        `Processing batch ${batchIndex + 1}/${dashboardBatches.length} (${batch.length} mailboxes)...`, result.conflicts_detected, result.alerts_created)

      // Collect all updates for this batch
      const batchUpdates = []
      
      for (const dashboardMailbox of batch) {
        const email = dashboardMailbox.email.toLowerCase()
        const sequencerMailbox = sequencerEmailMap.get(email)

        if (sequencerMailbox) {
          // Detect conflicts first (creates discrepancies)
          await this.detectConflicts(dashboardMailbox, sequencerMailbox, result)
          
          // Check for conflicts and prepare updates
          const updates = await this.prepareMailboxUpdates(dashboardMailbox, sequencerMailbox)
          if (Object.keys(updates).length > 0) {
            batchUpdates.push({
              email_account_id: sequencerMailbox.id,
              updates: updates
            })
          }
        } else {
          // Mailbox in dashboard but not in sequencer - could be intentional
          console.log(`📝 Mailbox ${email} exists in dashboard but not in ${sequencerType}`)
        }
      }

      // Process batch updates in a single Edge Function call
      if (batchUpdates.length > 0) {
        await this.processBatchUpdates(batchUpdates, result)
        result.mailboxes_synced += batchUpdates.length
      }
      
      processed += batch.length

      // Rate limiting between batches - much less frequent now
      if (batchIndex < dashboardBatches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000)) // 2 second delay between batches
      }
    }

    // Check for new mailboxes in sequencer (not in dashboard) - auto-import in batches
    const sequencerBatches = this.chunkArray(sequencerMailboxes, batchSize)
    
    for (let batchIndex = 0; batchIndex < sequencerBatches.length; batchIndex++) {
      const batch = sequencerBatches[batchIndex]
      
      if (this.abortSignal?.aborted) throw new Error('Sync cancelled')
      
      this.updateProgress('pulling_from_sequencer', processed, total, null,
        `Auto-importing batch ${batchIndex + 1}/${sequencerBatches.length} (${batch.length} mailboxes)...`, result.conflicts_detected, result.alerts_created)

      // Process batch sequentially to respect API rate limits
      for (const sequencerMailbox of batch) {
        const email = sequencerMailbox.from_email.toLowerCase()
        
        console.log(`[Sync Debug] Checking if ${email} exists in Dashboard...`)
        console.log(`[Sync Debug] Dashboard emails:`, Array.from(dashboardEmailMap.keys()))
        console.log(`[Sync Debug] Has ${email}?`, dashboardEmailMap.has(email))
        
        if (!dashboardEmailMap.has(email)) {
          await this.autoImportMailbox(sequencerMailbox, sequencerType, result)
          result.new_mailboxes_imported++
          result.imported_mailboxes.push(email)
        }
      }
      processed += batch.length

      // Rate limiting between batches - respect both Smartlead AND Supabase Edge Function limits
      if (batchIndex < sequencerBatches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 6000)) // 6 second delay = 10 requests per minute max
      }
    }
  }

  /**
   * Prepare mailbox updates by comparing dashboard and sequencer data
   */
  private async prepareMailboxUpdates(dashboardMailbox: any, sequencerMailbox: any): Promise<any> {
    const updates: any = {}

    // Compare signature (use proper comparison that ignores formatting differences)
    if (!areSignaturesEquivalent(dashboardMailbox.signature, sequencerMailbox.signature)) {
      updates.signature = formatSignatureForSync(dashboardMailbox.signature)
    }

    // Compare daily limit (use message_per_day from database)
    if (dashboardMailbox.message_per_day !== sequencerMailbox.max_email_per_day) {
      updates.max_email_per_day = dashboardMailbox.message_per_day
    }

    // Compare warmup status
    const dashboardWarmupActive = dashboardMailbox.warmup_status === 'active'
    const sequencerWarmupActive = sequencerMailbox.warmup_enabled === true || sequencerMailbox.warmup_enabled === 'true'
    
    if (dashboardWarmupActive !== sequencerWarmupActive) {
      updates.warmup_enabled = dashboardWarmupActive
    }

    // Compare custom tracking domain
    if (dashboardMailbox.custom_tracking_domain !== sequencerMailbox.custom_tracking_url) {
      updates.custom_tracking_url = dashboardMailbox.custom_tracking_domain
    }

    // Compare from name
    if (dashboardMailbox.from_name !== sequencerMailbox.from_name) {
      updates.from_name = dashboardMailbox.from_name
    }

    // Compare minimum time gap (use minimum_time_gap from database)
    if (dashboardMailbox.minimum_time_gap !== sequencerMailbox.time_to_wait_in_mins) {
      updates.time_to_wait_in_mins = dashboardMailbox.minimum_time_gap
    }

    // Compare client ID
    if (dashboardMailbox.client_id !== sequencerMailbox.client_id) {
      updates.client_id = dashboardMailbox.client_id
    }

    return updates
  }

  /**
   * Detect conflicts between dashboard and sequencer data
   * Creates discrepancies when Smartlead values differ from dashboard
   */
  private async detectConflicts(dashboardMailbox: any, sequencerMailbox: any, result: any): Promise<void> {
    const conflicts: string[] = []

    // Check warmup status conflict
    const dashboardWarmupActive = dashboardMailbox.warmup_status === 'active'
    const sequencerWarmupActive = sequencerMailbox.warmup_enabled === true || sequencerMailbox.warmup_enabled === 'true'
    
    if (dashboardWarmupActive !== sequencerWarmupActive) {
      conflicts.push(`Warmup Status: Dashboard=${dashboardMailbox.warmup_status}, Smartlead=${sequencerWarmupActive ? 'enabled' : 'disabled'}`)
    }

    // Check daily limit conflict (use message_per_day from database)
    if (dashboardMailbox.message_per_day !== sequencerMailbox.max_email_per_day) {
      conflicts.push(`Daily Limit: Dashboard=${dashboardMailbox.message_per_day}, Smartlead=${sequencerMailbox.max_email_per_day}`)
    }

    // Check signature conflict (use proper comparison that ignores formatting differences)
    if (!areSignaturesEquivalent(dashboardMailbox.signature, sequencerMailbox.signature)) {
      conflicts.push(`Signature: Dashboard="${dashboardMailbox.signature}", Smartlead="${sequencerMailbox.signature}"`)
    }

    // Check custom tracking domain conflict
    if (dashboardMailbox.custom_tracking_domain !== sequencerMailbox.custom_tracking_url) {
      conflicts.push(`Custom Tracking Domain: Dashboard="${dashboardMailbox.custom_tracking_domain}", Smartlead="${sequencerMailbox.custom_tracking_url}"`)
    }

    // Check from name conflict
    if (dashboardMailbox.from_name !== sequencerMailbox.from_name) {
      conflicts.push(`From Name: Dashboard="${dashboardMailbox.from_name}", Smartlead="${sequencerMailbox.from_name}"`)
    }

    // Check minimum time gap conflict (use minimum_time_gap from database)
    if (dashboardMailbox.minimum_time_gap !== sequencerMailbox.time_to_wait_in_mins) {
      conflicts.push(`Minimum Time Gap: Dashboard=${dashboardMailbox.minimum_time_gap}, Smartlead=${sequencerMailbox.time_to_wait_in_mins}`)
    }

    // Check client ID conflict
    if (dashboardMailbox.client_id !== sequencerMailbox.client_id) {
      conflicts.push(`Client ID: Dashboard=${dashboardMailbox.client_id}, Smartlead=${sequencerMailbox.client_id}`)
    }

    // If conflicts exist, create discrepancy record
    if (conflicts.length > 0) {
      console.log(`🚨 Conflicts detected for ${dashboardMailbox.email}:`, conflicts)
      
      // Create discrepancy record in database
      const { error: discrepancyError } = await supabase
        .from('mailbox_activity_discrepancies')
        .insert({
          mailbox_id: dashboardMailbox.id,
          sequencer_platform: 'smartlead',
          discrepancy_type: 'field_mismatch',
          severity: 'medium',
          status: 'pending',
          check_date: new Date().toISOString().split('T')[0],
          check_timestamp: new Date().toISOString()
        })

      if (discrepancyError) {
        console.error(`❌ Failed to create discrepancy record: ${discrepancyError.message}`)
      } else {
        result.conflicts_detected++
        console.log(`✅ Created discrepancy record for ${dashboardMailbox.email}`)
      }
    }
  }

  /**
   * Process batch updates using the new batch-update Edge Function
   */
  private async processBatchUpdates(batchUpdates: any[], result: any): Promise<void> {
    try {
      console.log(`📤 Batch updating ${batchUpdates.length} mailboxes to Smartlead`)

      const response = await supabase.functions.invoke('smartlead-mailboxes', {
        body: {
          action: 'batch-update',
          updates: batchUpdates
        }
      })

      if (response.error || !response.data.success) {
        console.error('❌ Batch update failed:', response.error || response.data.error)
        throw new Error(`Batch update failed: ${response.error || response.data.error}`)
      }

      const { successful, failed, successCount, errorCount } = response.data.data
      
      console.log(`✅ Batch update completed: ${successCount} successful, ${errorCount} failed`)
      
      if (failed.length > 0) {
        console.warn('⚠️ Some updates failed:', failed)
        // You could add these to result.errors or handle them as needed
      }

    } catch (error) {
      console.error('❌ Failed to batch update mailboxes:', error)
      throw error
    }
  }

  /**
   * Sync a single mailbox bidirectionally
   * Dashboard always wins, but conflicts are logged
   */
  private async syncMailboxBidirectional(
    dashboardMailbox: any,
    sequencerMailbox: SmartleadMailboxDetails,
    sequencerType: SequencerType,
    result: TwoWaySyncResult
  ): Promise<void> {
    const conflicts: SyncConflict[] = []

    // Get or create sequencer connection record
    const connection = await this.getOrCreateConnection(dashboardMailbox.id, sequencerType, sequencerMailbox.id)

    // Check for conflicts in syncable fields
    for (const dashboardField of SYNCABLE_DASHBOARD_FIELDS) {
      const sequencerField = DASHBOARD_TO_SMARTLEAD_FIELD_MAP[dashboardField]
      if (!sequencerField) continue

      const dashboardValue = dashboardMailbox[dashboardField]
      const sequencerValue = (sequencerMailbox as any)[sequencerField] || (sequencerMailbox as any)[dashboardField]

      // Check if values differ
      if (dashboardValue != sequencerValue && dashboardValue !== null && sequencerValue !== undefined) {
        // Check if both have been updated since last sync
        // Note: last_sync_at column doesn't exist in database schema
        // const lastSyncAt = connection.last_sync_at ? new Date(connection.last_sync_at) : null
        const lastSyncAt = null
        const dashboardUpdatedAt = dashboardMailbox.updated_at ? new Date(dashboardMailbox.updated_at) : null
        const sequencerUpdatedAt = sequencerMailbox.updated_at ? new Date(sequencerMailbox.updated_at) : null

        let isConflict = false
        
        if (lastSyncAt && dashboardUpdatedAt && sequencerUpdatedAt) {
          // Both updated since last sync = conflict
          if (dashboardUpdatedAt > lastSyncAt && sequencerUpdatedAt > lastSyncAt) {
            isConflict = true
          }
        }

        if (isConflict) {
          conflicts.push({
            mailbox_id: dashboardMailbox.id,
            email: dashboardMailbox.email,
            field: dashboardField,
            dashboard_value: dashboardValue,
            sequencer_value: sequencerValue,
            // Note: dashboard_updated_at column doesn't exist in database schema
            // dashboard_updated_at: dashboardMailbox.updated_at,
            sequencer_updated_at: sequencerMailbox.updated_at,
            resolution: 'dashboard_wins',
          })
          
          console.log(`⚠️ Conflict detected for ${dashboardMailbox.email}.${dashboardField}: Dashboard="${dashboardValue}" vs Sequencer="${sequencerValue}" - Dashboard wins`)
        }
      }
    }

    // Push dashboard changes to sequencer (Dashboard always wins)
    const pushResult = await this.pushToSequencer(dashboardMailbox, sequencerMailbox, sequencerType)
    
    if (pushResult.success && pushResult.fields_updated.length > 0) {
      console.log(`✅ Pushed ${pushResult.fields_updated.length} fields to ${sequencerType} for ${dashboardMailbox.email}`)
    }

    // Pull sequencer-only fields (warmup, metrics, etc) to dashboard
    await this.pullSequencerMetrics(dashboardMailbox, sequencerMailbox, sequencerType)

    // Update connection record
    // Note: Many columns don't exist in database schema, so we'll skip connection updates for now
    // await this.updateConnectionRecord(connection.id, {
    //   last_sync_at: new Date().toISOString(),
    //   last_sync_direction: 'bidirectional',
    //   // Note: dashboard_updated_at column doesn't exist in database schema
    //   // dashboard_updated_at: dashboardMailbox.updated_at,
    //   sequencer_updated_at: sequencerMailbox.updated_at,
    //   sync_status: conflicts.length > 0 ? 'conflict' : 'synced',
    // })

    // Track conflicts
    if (conflicts.length > 0) {
      result.conflicts.push(...conflicts)
      result.conflicts_detected += conflicts.length
      result.conflicts_resolved += conflicts.length // Dashboard wins = auto-resolved
    }
  }

  /**
   * Push dashboard changes to sequencer
   * Dashboard is source of truth
   */
  private async pushToSequencer(
    dashboardMailbox: any,
    sequencerMailbox: SmartleadMailboxDetails,
    sequencerType: SequencerType
  ): Promise<DashboardToSequencerPushResult> {
    const result: DashboardToSequencerPushResult = {
      success: false,
      mailbox_email: dashboardMailbox.email,
      sequencer_type: sequencerType,
      fields_updated: [],
      conflicts_detected: [],
      error: null,
    }

    try {
      if (sequencerType === 'smartlead') {
        const payload: SmartleadUpdatePayload = {}
        const fieldsToUpdate: string[] = []

        // Build update payload for fields that differ
        if (dashboardMailbox.daily_limit != sequencerMailbox.message_per_day) {
          payload.max_email_per_day = dashboardMailbox.daily_limit
          fieldsToUpdate.push('daily_limit')
        }
        
        if (!areSignaturesEquivalent(dashboardMailbox.signature, sequencerMailbox.signature)) {
          payload.signature = formatSignatureForSync(dashboardMailbox.signature) || ''
          fieldsToUpdate.push('signature')
        }
        
        // Note: bcc_email column doesn't exist in database schema
        // if (dashboardMailbox.bcc_email != sequencerMailbox.bcc_email) {
        //   payload.bcc = dashboardMailbox.bcc_email || ''
        //   fieldsToUpdate.push('bcc_email')
        // }
        
        if (dashboardMailbox.custom_tracking_domain != sequencerMailbox.custom_tracking_domain) {
          payload.custom_tracking_url = dashboardMailbox.custom_tracking_domain || ''
          fieldsToUpdate.push('custom_tracking_domain')
        }

        // Only push if there are changes
        if (Object.keys(payload).length > 0) {
          console.log(`📤 Pushing ${Object.keys(payload).length} fields to Smartlead for ${dashboardMailbox.email}`)
          
          // Ultra-conservative rate limiting for both Smartlead AND Supabase Edge Functions
          // Smartlead: 10 requests per 2 seconds, Supabase Edge Functions: ~10 requests per minute
          // Use 6 second delay to be safe with both APIs
          await new Promise(resolve => setTimeout(resolve, 6000)) // 6 second delay between API calls
          
          const response = await supabase.functions.invoke('smartlead-mailboxes', {
            body: {
              action: 'update',
              email_account_id: sequencerMailbox.id,
              updates: payload,
            }
          })

          if (response.error) {
            throw new Error(`Smartlead API error: ${response.error.message}`)
          }

          if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to update Smartlead')
          }

          result.success = true
          result.fields_updated = fieldsToUpdate
          console.log(`✅ Successfully pushed to Smartlead: ${fieldsToUpdate.join(', ')}`)
        } else {
          result.success = true
          console.log(`📝 No changes needed for ${dashboardMailbox.email}`)
        }
      }

      return result

    } catch (error) {
      console.error(`❌ Failed to push to ${sequencerType}:`, error)
      result.error = error instanceof Error ? error.message : 'Unknown error'
      return result
    }
  }

  /**
   * Pull sequencer-specific metrics to dashboard
   * These are read-only fields controlled by the sequencer
   */
  private async pullSequencerMetrics(
    dashboardMailbox: any,
    sequencerMailbox: SmartleadMailboxDetails,
    sequencerType: SequencerType
  ): Promise<void> {
    console.log(`[Sync Debug] Starting pullSequencerMetrics for: ${dashboardMailbox.email}`)
    console.log(`[Sync Debug] Full Smartlead data:`, JSON.stringify(sequencerMailbox, null, 2))
    const updates: any = {}

    // Update current daily sent count
    if (sequencerMailbox.daily_sent_count !== dashboardMailbox.current_daily_sent) {
      updates.current_daily_sent = sequencerMailbox.daily_sent_count
    }

    // Update SMTP/IMAP status
    if (sequencerMailbox.is_smtp_success !== dashboardMailbox.smtp_success) {
      updates.smtp_success = sequencerMailbox.is_smtp_success
    }
    
    if (sequencerMailbox.is_imap_success !== dashboardMailbox.imap_success) {
      updates.imap_success = sequencerMailbox.is_imap_success
    }

    // Update signature from Smartlead
    console.log(`[Sync Debug] Mailbox: ${dashboardMailbox.email}`)
    console.log(`[Sync Debug] Dashboard Signature: '${dashboardMailbox.signature}'`)
    console.log(`[Sync Debug] Smartlead Signature: '${sequencerMailbox.signature}'`)
    
    if (!areSignaturesEquivalent(sequencerMailbox.signature, dashboardMailbox.signature)) {
      console.log(`[Sync Debug] Signature Mismatch Detected! Updating Dashboard signature.`)
      updates.signature = sequencerMailbox.signature || ''
      console.log(`[Sync Debug] New Dashboard Signature (proposed): '${updates.signature}'`)
    } else {
      console.log(`[Sync Debug] Signatures match or no Smartlead signature. No update needed.`)
    }

    // Update warmup status from Smartlead
    if (sequencerMailbox.warmup_enabled !== undefined) {
      const smartleadWarmupStatus = sequencerMailbox.warmup_enabled ? 'active' : 'inactive'
      if (smartleadWarmupStatus !== dashboardMailbox.warmup_status) {
        console.log(`[Sync Debug] Warmup status mismatch: Dashboard='${dashboardMailbox.warmup_status}', Smartlead='${smartleadWarmupStatus}'`)
        updates.warmup_status = smartleadWarmupStatus
      }
    }

    // Update warmup details
    if (sequencerMailbox.warmup_details) {
      if (sequencerMailbox.warmup_details.warmup_reputation) {
        const reputation = parseInt(sequencerMailbox.warmup_details.warmup_reputation.replace('%', ''), 10) || 0
        if (reputation !== dashboardMailbox.warmup_reputation) {
          updates.warmup_reputation = reputation
        }
      }
    }

    // Auto-update status from ORDERED/PURCHASED to ACTIVE when successfully connected to Smartlead
    if (dashboardMailbox.status === 'ORDERED/PURCHASED' || dashboardMailbox.status === 'ORDERED' || dashboardMailbox.status === 'PURCHASED') {
      console.log(`[Sync Debug] Auto-updating status from '${dashboardMailbox.status}' to 'ACTIVE' for ${dashboardMailbox.email} (successfully connected to Smartlead)`)
      updates.status = 'ACTIVE'
      updates.status_updated_at = new Date().toISOString()
    }

    // Apply updates if any
    if (Object.keys(updates).length > 0) {
      console.log(`[Sync Debug] Applying updates to Dashboard:`, updates)
      updates.updated_at = new Date().toISOString()
      
      const { error } = await supabase
        .from('mailboxes')
        .update(updates)
        .eq('id', dashboardMailbox.id)

      if (error) {
        console.error(`❌ Failed to update dashboard mailbox ${dashboardMailbox.email}:`, error)
      } else {
        console.log(`✅ Pulled ${Object.keys(updates).length} metrics from ${sequencerType} for ${dashboardMailbox.email}`)
      }
    }
  }

  /**
   * Auto-import a mailbox found in sequencer but not in dashboard
   */
  private async autoImportMailbox(
    sequencerMailbox: SmartleadMailboxDetails,
    sequencerType: SequencerType,
    result: TwoWaySyncResult
  ): Promise<void> {
    try {
      const domain = sequencerMailbox.from_email.split('@')[1]
      const provider = this.detectProvider(sequencerMailbox.from_email)

      // Extract warmup details
      let warmupEnabled = false
      let warmupReputation = 0
      
      if (sequencerMailbox.warmup_details) {
        warmupEnabled = sequencerMailbox.warmup_details.status === 'ACTIVE'
        warmupReputation = parseInt(sequencerMailbox.warmup_details.warmup_reputation?.replace('%', '') || '0', 10)
      }

      const newMailbox = {
        id: crypto.randomUUID(),
        email: sequencerMailbox.from_email,
        from_name: sequencerMailbox.from_name,
        domain,
        provider,
        smartlead_id: sequencerType === 'smartlead' ? sequencerMailbox.id : null,
        daily_limit: sequencerMailbox.message_per_day || 50,
        current_daily_sent: sequencerMailbox.daily_sent_count || 0,
        signature: sequencerMailbox.signature,
        // Note: bcc_email column doesn't exist in database schema
        // bcc_email: sequencerMailbox.bcc_email,
        reply_to_email: sequencerMailbox.different_reply_to_address,
        custom_tracking_domain: sequencerMailbox.custom_tracking_domain,
        smtp_success: sequencerMailbox.is_smtp_success,
        imap_success: sequencerMailbox.is_imap_success,
        // Note: warmup_enabled column doesn't exist in database schema
        // warmup_enabled: warmupEnabled,
        warmup_reputation: warmupReputation,
        status: 'ACTIVE',
        notes: `Auto-imported from ${sequencerType} - Please review and complete missing information`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Note: last_synced column doesn't exist in database schema
        // last_synced: new Date().toISOString(),
      }

      // Check if mailbox already exists to prevent duplicate key errors
      const { data: existingMailbox } = await supabase
        .from('mailboxes')
        .select('id, email')
        .eq('email', sequencerMailbox.from_email)
        .maybeSingle()

      if (existingMailbox) {
        console.log(`📝 Mailbox ${sequencerMailbox.from_email} already exists in dashboard, skipping auto-import`)
        return
      }

      console.log(`🆕 Auto-importing new mailbox from ${sequencerType}: ${sequencerMailbox.from_email}`)
      const { data, error } = await supabase
        .from('mailboxes')
        .insert(newMailbox)
        .select()
        .single()

      if (error) {
        throw error
      }

      console.log(`🆕 Auto-imported mailbox from ${sequencerType}: ${sequencerMailbox.from_email}`)

      // Create connection record
      await this.getOrCreateConnection(data.id, sequencerType, sequencerMailbox.id)

    } catch (error) {
      console.error(`❌ Failed to auto-import mailbox ${sequencerMailbox.from_email}:`, error)
      result.errors.push(`Failed to import ${sequencerMailbox.from_email}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Check for activity discrepancies between dashboard and sequencer
   */
  private async checkActivityDiscrepancies(
    dashboardMailboxes: any[],
    sequencerType: SequencerType,
    result: TwoWaySyncResult
  ): Promise<void> {
    for (const mailbox of dashboardMailboxes) {
      if (this.abortSignal?.aborted) throw new Error('Sync cancelled')

      try {
        // Validate mailbox ID before querying
        if (!mailbox.id || typeof mailbox.id !== 'string') {
          console.log(`⚠️ Skipping discrepancy check for ${mailbox.email}: invalid mailbox ID`)
          continue
        }

        // Get sent count from dashboard obsync_dynamic_reports for TODAY only
        const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
        const { count: dashboardSentCount, error: activitiesError } = await supabase
          .from('obsync_dynamic_reports')
          .select('*', { count: 'exact', head: true })
          .eq('from_email', mailbox.email)
          .eq('event_type', 'EMAIL_SENT')
          .gte('created_at', `${today}T00:00:00.000Z`)
          .lt('created_at', `${today}T23:59:59.999Z`)

        if (activitiesError) {
          console.error(`❌ Error querying activities for ${mailbox.email}:`, activitiesError)
          continue
        }

        // Get sent count from sequencer (via connection or direct API)
        const sequencerSentCount = mailbox.current_daily_sent || 0 // This should be fetched from sequencer

        console.log(`🔍 [Discrepancy Check] ${mailbox.email}: Dashboard=${dashboardSentCount}, Smartlead=${sequencerSentCount}, Date=${today}`)

        const difference = Math.abs((dashboardSentCount || 0) - sequencerSentCount)
        const dashboardTotal = dashboardSentCount || 1
        const differencePercentage = (difference / dashboardTotal) * 100

        // If discrepancy is significant (>10%)
        if (differencePercentage > 10 && difference > 5) {
          console.log(`⚠️ Discrepancy detected for ${mailbox.email}: Dashboard=${dashboardSentCount}, ${sequencerType}=${sequencerSentCount}`)
          
          const severity = this.calculateDiscrepancySeverity(differencePercentage, difference)

          const discrepancy = {
            id: crypto.randomUUID(),
            mailbox_id: mailbox.id,
            sequencer_platform: sequencerType,
            check_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD
            check_timestamp: new Date().toISOString(),
            expected_sent_count: dashboardSentCount || 0,
            actual_sent_count: sequencerSentCount,
            discrepancy_amount: difference,
            discrepancy_percentage: differencePercentage,
            severity,
            status: 'pending',
            alerted_at: null,
            alert_sent_to_slack: false,
            slack_message_ts: null,
            resolved_at: null,
            resolved_by: null,
            resolution_notes: null,
            metadata: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }

          // Save discrepancy to database
          await supabase.from('mailbox_activity_discrepancies').insert(discrepancy)

          result.discrepancies.push(discrepancy)
          result.discrepancies_found++
        }

      } catch (error) {
        console.error(`❌ Failed to check discrepancy for ${mailbox.email}:`, error)
      }
    }
  }

  /**
   * Create alerts for conflicts, imports, and discrepancies
   */
  private async createAlerts(result: TwoWaySyncResult, sequencerType: SequencerType): Promise<void> {
    const alerts: any[] = []

    // Create conflict alerts
    for (const conflict of result.conflicts) {
      alerts.push({
        id: crypto.randomUUID(),
        alert_type: 'conflict',
        severity: 'warning',
        status: 'unread',
        mailbox_id: conflict.mailbox_id,
        sequencer_platform: sequencerType,
        title: `Sync conflict: ${conflict.email}`,
        description: `Field "${conflict.field}" changed in both Dashboard and ${sequencerType}. Dashboard value applied.`,
        metadata: conflict,
        slack_sent: false,
        slack_channel: null,
        slack_message_ts: null,
        slack_error: null,
        slack_sent_at: null,
        resolved_at: null,
        resolved_by: null,
        resolution_notes: null,
        resolution_action: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        expires_at: null,
      })
    }

    // Create import alerts
    for (const email of result.imported_mailboxes) {
      alerts.push({
        id: crypto.randomUUID(),
        alert_type: 'auto_import',
        severity: 'info',
        status: 'unread',
        mailbox_id: null,
        sequencer_platform: sequencerType,
        title: `New mailbox: ${email}`,
        description: `Found in ${sequencerType} and auto-imported. Please review and complete information.`,
        metadata: { email },
        slack_sent: false,
        slack_channel: null,
        slack_message_ts: null,
        slack_error: null,
        slack_sent_at: null,
        resolved_at: null,
        resolved_by: null,
        resolution_notes: null,
        resolution_action: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        expires_at: null,
      })
    }

    // Create discrepancy alerts
    for (const discrepancy of result.discrepancies) {
      alerts.push({
        id: crypto.randomUUID(),
        alert_type: 'discrepancy',
        severity: discrepancy.severity === 'critical' ? 'critical' : 'warning',
        status: 'unread',
        mailbox_id: discrepancy.mailbox_id,
        sequencer_platform: sequencerType,
        title: `Activity mismatch detected`,
        description: `Sent count discrepancy: Dashboard=${discrepancy.expected_sent_count}, ${sequencerType}=${discrepancy.actual_sent_count} (${discrepancy.discrepancy_percentage.toFixed(1)}% diff)`,
        metadata: discrepancy,
        slack_sent: false,
        slack_channel: null,
        slack_message_ts: null,
        slack_error: null,
        slack_sent_at: null,
        resolved_at: null,
        resolved_by: null,
        resolution_notes: null,
        resolution_action: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        expires_at: null,
      })
    }

    // Insert all alerts
    if (alerts.length > 0) {
      const { error } = await supabase.from('sync_alerts').insert(alerts)
      
      if (error) {
        console.error('❌ Failed to create alerts:', error)
      } else {
        result.alerts_created = alerts.length
        console.log(`✅ Created ${alerts.length} alerts`)
        
        // Send alerts to Slack
        try {
          const { slackAlertService } = await import('./slack-alert-service')
          await slackAlertService.processUnsentAlerts()
          console.log('✅ Slack alerts sent')
        } catch (slackError) {
          console.error('❌ Failed to send Slack alerts:', slackError)
        }
      }
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // HELPER METHODS
  // ═════════════════════════════════════════════════════════════════════════

  private async fetchDashboardMailboxes(): Promise<any[]> {
    const { data, error } = await supabase
      .from('mailboxes')
      .select('*')
      // Include all mailboxes, not just ACTIVE ones, to prevent duplicate imports
      .in('status', ['ACTIVE', 'ORDERED/PURCHASED', 'INACTIVE'])

    if (error) {
      throw new Error(`Failed to fetch dashboard mailboxes: ${error.message}`)
    }

    return data || []
  }

  private async fetchSequencerMailboxes(sequencerType: SequencerType): Promise<SmartleadMailboxDetails[]> {
    if (sequencerType === 'smartlead') {
      const allMailboxes: SmartleadMailboxDetails[] = []
      let page = 1
      const limit = 100

      while (true) {
        const response = await supabase.functions.invoke('smartlead-mailboxes', {
          body: { action: 'list', page, limit }
        })

        if (response.error || !response.data.success) {
          throw new Error(`Failed to fetch ${sequencerType} mailboxes`)
        }

        const batch = response.data.data || []
        if (batch.length === 0) break

        allMailboxes.push(...batch)
        
        // Check if there are more pages
        const hasMore = response.data.pagination?.hasMore
        if (!hasMore || batch.length < limit) break
        
        page++
        
        // Rate limiting to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      console.log(`📊 Fetched ${allMailboxes.length} total mailboxes from Smartlead`)
      return allMailboxes
    }

    return []
  }

  private async getOrCreateConnection(
    mailboxId: string,
    sequencerType: SequencerType,
    sequencerAccountId: number
  ): Promise<MailboxSequencerConnection> {
    // Try to find existing connection (using DB column name)
    const { data: existing, error: fetchError } = await supabase
      .from('mailbox_sequencer_connections')
      .select('*')
      .eq('mailbox_id', mailboxId)
      .eq('sequencer_platform', sequencerType)
      .maybeSingle()

    if (fetchError) {
      console.error('Error fetching connection:', fetchError)
    }

    if (existing) {
      return existing as MailboxSequencerConnection
    }

    // Create new connection (using DB column names)
    const newConnection = {
      id: crypto.randomUUID(),
      mailbox_id: mailboxId,
      sequencer_platform: sequencerType,
      sequencer_mailbox_id: String(sequencerAccountId),
      is_active: true,
      // Note: sync_status column doesn't exist in database schema
      // sync_status: 'synced' as const,
      // Note: last_synced_at and last_synced_from columns don't exist in database schema
      // last_synced_at: new Date().toISOString(),
      // last_synced_from: 'sequencer',
      warmup_status: null,
      warmup_reputation: null,
      warmup_sent_total: null,
      warmup_spam_total: null,
      current_daily_sent: null,
      bounce_rate: null,
      spam_score: null,
      connection_health: null,
      smtp_success: null,
      imap_success: null,
      last_connection_check: null,
      connection_error: null,
      sync_error: null,
      sync_attempt_count: 0,
      sequencer_metadata: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('mailbox_sequencer_connections')
      .insert(newConnection)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create connection: ${error.message}`)
    }

    return data as MailboxSequencerConnection
  }

  private async updateConnectionRecord(connectionId: string, updates: Partial<MailboxSequencerConnection>): Promise<void> {
    const { error } = await supabase
      .from('mailbox_sequencer_connections')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', connectionId)

    if (error) {
      console.error('Failed to update connection record:', error)
    }
  }

  private calculateDiscrepancySeverity(percentage: number, absoluteDiff: number): 'low' | 'medium' | 'high' | 'critical' {
    if (percentage > 50 || absoluteDiff > 1000) return 'critical'
    if (percentage > 30 || absoluteDiff > 500) return 'high'
    if (percentage > 20 || absoluteDiff > 100) return 'medium'
    return 'low'
  }

  private detectProvider(email: string): string {
    const domain = email.split('@')[1]?.toLowerCase() || ''
    if (domain.includes('gmail')) return 'Gmail'
    if (domain.includes('outlook') || domain.includes('hotmail')) return 'Outlook'
    if (domain.includes('yahoo')) return 'Yahoo'
    if (domain.includes('zoho')) return 'Zoho'
    return 'Other'
  }

  private updateProgress(
    phase: TwoWaySyncProgress['phase'],
    processed: number,
    total: number,
    currentMailbox: string | null,
    message: string,
    conflictsFound: number,
    alertsCreated: number
  ): void {
    if (this.progressCallback) {
      this.progressCallback({
        phase,
        processed,
        total,
        percentage: total > 0 ? (processed / total) * 100 : 0,
        current_mailbox: currentMailbox,
        message,
        conflicts_found: conflictsFound,
        alerts_created: alertsCreated,
      })
    }
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTED SERVICE INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

export const mailboxTwoWaySyncService = {
  performFullSync: async (
    sequencerType: SequencerType = 'smartlead',
    progressCallback?: TwoWaySyncProgressCallback,
    abortSignal?: AbortSignal
  ): Promise<TwoWaySyncResult> => {
    const service = new MailboxTwoWaySyncService(progressCallback)
    return service.performFullSync(sequencerType, abortSignal)
  }
}

